'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LogIn, Zap, Lock, Eye, Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePageAccess } from '@/context/SiteContext';
import { getPublicContentPreview, markContentPreviewConsumed } from '@/lib/api-client';

type OverlayType = 'login' | 'pro' | null;

export default function ContentPreviewOverlay() {
  const pathname = usePathname();
  const { user, isPro, isFull, isAdmin, loading } = useAuth();
  const { getAccess, loaded } = usePageAccess();

  const [previewEnabled, setPreviewEnabled] = useState(false);
  const [previewSeconds, setPreviewSeconds] = useState(5);
  const [canPreview, setCanPreview] = useState(false);
  const [overlayType, setOverlayType] = useState<OverlayType>(null);
  const [countdown, setCountdown] = useState(0);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const consumedRef = useRef(false);

  // Fetch content preview settings on mount
  useEffect(() => {
    getPublicContentPreview()
      .then((data) => {
        setPreviewEnabled(data.enabled);
        setPreviewSeconds(data.previewSeconds);
        setCanPreview(data.canPreview);
        setSettingsLoaded(true);
      })
      .catch(() => {
        setSettingsLoaded(true);
      });
  }, []);

  // Determine what overlay to show based on page access and user state
  const getOverlayType = useCallback((): OverlayType => {
    if (!loaded) return null;

    // Skip auth-related and admin pages
    if (
      pathname.startsWith('/admin') ||
      pathname.startsWith('/login') ||
      pathname.startsWith('/create-account') ||
      pathname.startsWith('/forgot-password') ||
      pathname.startsWith('/reset-password') ||
      pathname.startsWith('/verify-email') ||
      pathname === '/pro'
    ) return null;

    const access = getAccess(pathname);

    if (access === 'public') return null;

    if (access === 'registered') {
      // Only show login overlay if user is not logged in
      if (!user) return 'login';
      return null;
    }

    if (access === 'pro') {
      // Anonymous user -> login overlay
      if (!user) return 'login';
      // Logged in but not pro/full -> pro overlay
      if (!isPro && !isFull) return 'pro';
      return null;
    }

    if (access === 'full') {
      // Anonymous user -> login overlay
      if (!user) return 'login';
      // Logged in but not full -> pro overlay
      if (!isFull) return 'pro';
      return null;
    }

    return null;
  }, [pathname, loaded, user, isPro, isFull, getAccess]);

  // Handle preview timer and overlay display
  useEffect(() => {
    // Clear any previous timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Reset overlay on navigation
    setOverlayType(null);
    consumedRef.current = false;

    if (loading || !loaded || !settingsLoaded) return;
    if (isAdmin) return;
    if (!previewEnabled) return;

    const type = getOverlayType();
    if (!type) return;

    if (!canPreview) {
      // Preview already consumed (within expiry window) — show overlay immediately
      setOverlayType(type);
      return;
    }

    // Start countdown
    setCountdown(previewSeconds);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          timerRef.current = null;
          // Show overlay and mark preview consumed
          setOverlayType(type);
          if (!consumedRef.current) {
            consumedRef.current = true;
            setCanPreview(false);
            markContentPreviewConsumed().catch(() => {});
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [pathname, loading, loaded, settingsLoaded, isAdmin, previewEnabled, canPreview, previewSeconds, getOverlayType]);

  // Don't render anything if no overlay needed
  if (!overlayType) {
    // Show countdown indicator during preview
    if (countdown > 0 && previewEnabled && canPreview) {
      return (
        <div className="fixed bottom-4 right-4 z-[90] flex items-center gap-2 bg-card-bg/90 border border-default rounded-lg px-3 py-2 shadow-lg backdrop-blur-sm">
          <Eye size={14} className="text-sky-400" />
          <span className="text-xs text-slate-300">Preview ends in <span className="text-white font-semibold">{countdown}s</span></span>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="fixed inset-0 z-[99] flex items-center justify-center pointer-events-auto">
      {/* Semi-transparent backdrop */}
      <div className="absolute inset-0 bg-surface/60 backdrop-blur-sm" />

      {/* Overlay card */}
      <div className="relative z-10 max-w-md w-full mx-4 bg-card-bg border border-default rounded-2xl p-8 shadow-2xl text-center">
        {overlayType === 'login' ? <LoginOverlayContent /> : <ProOverlayContent />}
      </div>
    </div>
  );
}

function LoginOverlayContent() {
  const pathname = usePathname();

  return (
    <>
      <div className="w-16 h-16 rounded-2xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center mx-auto mb-5">
        <Lock size={28} className="text-sky-400" />
      </div>
      <h2 className="text-xl font-bold text-white mb-3">Sign In Required</h2>
      <p className="text-fg-muted text-sm leading-relaxed mb-6">
        Create a free account or sign in to continue viewing this content.
      </p>
      <div className="flex flex-col gap-3">
        <Link
          href={`/login?returnTo=${encodeURIComponent(pathname)}`}
          className="flex items-center justify-center gap-2 px-5 py-3 bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <LogIn size={16} />
          Sign In
        </Link>
        <Link
          href={`/create-account?returnTo=${encodeURIComponent(pathname)}`}
          className="flex items-center justify-center gap-2 px-5 py-3 bg-surface hover:bg-card-bg border border-default text-slate-300 text-sm font-medium rounded-lg transition-colors"
        >
          Create Free Account
        </Link>
      </div>
      <p className="text-xs text-slate-600 mt-4">
        Free accounts get access to all registered content.
      </p>
    </>
  );
}

function ProOverlayContent() {
  return (
    <>
      <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-5">
        <Zap size={28} className="text-amber-400" />
      </div>
      <h2 className="text-xl font-bold text-white mb-3">Upgrade to Pro</h2>
      <p className="text-fg-muted text-sm leading-relaxed mb-6">
        This content requires a Pro or Full subscription. Upgrade to unlock advanced features, real-time data, and more.
      </p>
      <div className="flex flex-col gap-3">
        <Link
          href="/pro"
          className="flex items-center justify-center gap-2 px-5 py-3 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <Zap size={16} />
          View Plans & Pricing
        </Link>
      </div>
      <div className="flex items-center justify-center gap-4 mt-5 text-xs text-fg-subtle">
        <span className="flex items-center gap-1"><Check size={12} className="text-green-400" /> 7-day free trial</span>
        <span className="flex items-center gap-1"><Check size={12} className="text-green-400" /> Cancel anytime</span>
      </div>
    </>
  );
}

